/* import __COLOCATED_TEMPLATE__ from './question-table.hbs'; */
/* RESPONSIBLE TEAM: team-ai-agent-2 */
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import type IntlService from 'ember-intl/services/intl';
import { action } from '@ember/object';
import {
  type PlaygroundQuestion,
  CustomerAnswerRating,
  Status,
} from 'embercom/lib/fin-playground/question';
import { tracked } from '@glimmer/tracking';
import type PlaygroundApi from 'embercom/services/playground-api';
interface Args {
  selectedQuestion?: PlaygroundQuestion;
  selectedQuestionIndex: number;
  onRowClick: (questionText: string) => void;
  isDetailsModuleOpen: boolean;
}

export default class QuestionTable extends Component<Args> {
  @service declare intl: IntlService;
  @service declare intercomEventService: $TSFixMe;
  @service declare playgroundApi: PlaygroundApi;
  @tracked hoveredRowIndex: number | null = null;
  @tracked lastSelectedIndex: number | null = null;
  @tracked lastSelectionAction: boolean | null = null;

  stamp = (
    questionStatus: Status,
    questionAnswerRating: CustomerAnswerRating | null,
  ): { text: string; color: 'red' | 'green' | 'gray' | 'yellow'; class?: string } | null => {
    if (questionStatus === Status.Completed) {
      switch (questionAnswerRating) {
        case CustomerAnswerRating.Positive:
          return {
            text: this.intl.t(`ai-agent.playground.answer-rating.positive`),
            color: 'green',
          };
        case CustomerAnswerRating.Negative:
          return {
            text: this.intl.t(`ai-agent.playground.answer-rating.negative`),
            color: 'red',
          };
        case CustomerAnswerRating.Acceptable:
          return {
            text: this.intl.t(`ai-agent.playground.answer-rating.acceptable`),
            color: 'yellow',
          };
        default:
          // ie, the rating is null
          return {
            text: this.intl.t(`ai-agent.playground.answer-rating.needs-rating`),
            color: 'gray',
            class: 'border-neutral-border-emphasis',
          };
      }
    }

    // otherwise (eg running), don't show a stamp
    return null;
  };

  trackBulkSelect(selectingOrDeselecting: 'selecting' | 'deselecting', bulkSelecting: boolean) {
    this.intercomEventService.trackAnalyticsEvent({
      action: 'clicked',
      object: 'bulk_select',
      context: 'playground',
      selecting_or_deselecting: selectingOrDeselecting,
      bulk_selecting: bulkSelecting,
    });
  }

  get questions() {
    return this.playgroundApi.playground?.filteredQuestions ?? [];
  }

  get bulkSelectedQuestions() {
    return this.playgroundApi.playground?.bulkSelectedQuestions ?? [];
  }

  get areAllNonRunningQuestionsSelected() {
    return this.questions.every(
      (question) => question.status === 'running' || question.isBulkSelected,
    );
  }

  get areAllQuestionsRunning() {
    return this.questions.every((question) => question.status === 'running');
  }

  @action
  handleSelect(question: PlaygroundQuestion, index: number, event: MouseEvent) {
    event.stopPropagation();

    if (event.shiftKey && this.lastSelectedIndex !== null && this.lastSelectedIndex !== index) {
      this.handleShiftSelect(index);
      return;
    }

    question.isBulkSelected = !question.isBulkSelected;
    this.lastSelectedIndex = index;
    this.lastSelectionAction = question.isBulkSelected;
    this.trackBulkSelect(question.isBulkSelected ? 'selecting' : 'deselecting', false);
  }

  private handleShiftSelect(newIndex: number) {
    let questions = this.questions;
    let lastIndex = this.lastSelectedIndex!;
    let shouldSelect = this.lastSelectionAction!;

    let start = Math.min(lastIndex, newIndex);
    let end = Math.max(lastIndex, newIndex);

    let questionsInRange = questions.slice(start, end + 1).filter((q) => q.status !== 'running');

    questionsInRange.forEach((q) => {
      q.isBulkSelected = shouldSelect;
    });

    this.lastSelectedIndex = newIndex;
    this.trackBulkSelect(shouldSelect ? 'selecting' : 'deselecting', false);
  }

  @action
  onBulkSelect(event: Event) {
    event.stopPropagation();

    if (this.areAllNonRunningQuestionsSelected) {
      this.resetBulkSelect();
      this.trackBulkSelect('deselecting', true);
    } else if (this.bulkSelectedQuestions.length >= 0) {
      this.questions.forEach((question) => {
        if (question.status !== 'running') {
          question.isBulkSelected = true;
        }
      });
      this.trackBulkSelect('selecting', true);
    }

    this.lastSelectedIndex = null;
    this.lastSelectionAction = null;
  }

  @action
  resetBulkSelect() {
    this.questions.forEach((question) => {
      question.isBulkSelected = false;
    });
    this.lastSelectedIndex = null;
    this.lastSelectionAction = null;
  }

  @action
  setHoveredIndex(index: number | null) {
    this.hoveredRowIndex = index;
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'AiAgent::Playground::QuestionTable': typeof QuestionTable;
  }
}
